/**
 * Reusable Zod schema for math grading policies, shared by every widget whose blanks/answers are
 * graded by the Compute Engine (today: Cloze Math; designed so other widgets can compose the same
 * fields). Kept engine-free — it only references the ID list and the metadata table — so schemas
 * and studio UI import it without pulling `@cortex-js/compute-engine` into their bundles.
 *
 * The matching predicates live in `mathEquivalence.ts`; the clash rules live in `MATH_POLICY_META`
 * (`mathPolicyIds.ts`). This file is the persistence/validation gate that ties them together.
 */
import { z } from 'zod';

import { hasOnlyEmptySlots } from './mathAnswerSlot';
import { MATH_KEYBOARD_IDS } from './mathKeyboardIds';
import {
  MATH_CONNECTIVES,
  MATH_POLICY_IDS,
  MATH_POLICY_META,
  MATH_SYMBOLIC_EXCEPTIONS,
  SUPPORTED_SYMBOLIC_EXCEPTIONS,
  type MathPolicyExpression,
  type MathPolicyId,
  type MathPolicyLiteral
} from './mathPolicyIds';

// A form-only policy (`combined`/`partiallyCombined`/`reduced`) never compares the answer's VALUE, so
// alone it'd accept a right-form wrong-value answer — an expression that uses one must also carry a
// value-bearing term. Sourced from `MATH_POLICY_META` so a new form-only policy only sets the flag once.
const isFormOnly = (policy: MathPolicyId): boolean => MATH_POLICY_META[policy].formOnly === true;

/** One term in a blank's grading expression — see {@link MathPolicyLiteral}. */
export const MathPolicyLiteralSchema = z.object({
  policy: z.enum(MATH_POLICY_IDS),
  negate: z.boolean().optional(),
  // 'approx' only: digits after the decimal point to compare.
  precisionDigits: z.number().int().min(0).max(10).optional(),
  // Bound policies only: the constant to compare against (number/LaTeX string).
  bound: z.string().min(1).optional(),
  // 'partiallyCombined' only: which symbolic forms may stay un-evaluated (√ roots / π).
  allowSymbolic: z.array(z.enum(MATH_SYMBOLIC_EXCEPTIONS)).min(1).optional()
}) satisfies z.ZodType<MathPolicyLiteral>;

/**
 * Connective-aware clash validation for a grading expression. The rules DEPEND on the connective —
 * the same pair that contradicts under `and` is perfectly valid under `or`:
 *  - **Under `and`** (every term must hold): at most one un-negated policy per {@link MathPolicyGroup}
 *    — two would contradict (`decimal` ∧ `fraction`) or be redundant (a value basis is implied).
 *  - **Under `or`** (any term): group-exclusivity does NOT apply — `decimal OR fraction` ("accept
 *    either form") is exactly what an author wants.
 *  - **Both connectives:** NOT only on `negatable` (non-value-bearing) policies; no policy paired with
 *    its own negation; no duplicate literal.
 * Redundancy (e.g. `equivalent` ∧ `fraction`) is a UI-level warning, not blocked here.
 */
export function validateMathPolicyExpression(expr: MathPolicyExpression, ctx: z.RefinementCtx): void {
  const { connective, terms } = expr;
  const seen = new Set<string>();
  const usedGroups = new Set<string>();

  terms.forEach((literal, index) => {
    const meta = MATH_POLICY_META[literal.policy];

    if (literal.negate && !meta.negatable) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['terms', index, 'negate'],
        message: `"${literal.policy}" cannot be negated.`
      });
    }

    if (meta.needsBound && !literal.bound) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['terms', index, 'bound'],
        message: `"${literal.policy}" needs a value to compare against.`
      });
    }
    if (!meta.needsBound && literal.bound != null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['terms', index, 'bound'],
        message: `"${literal.policy}" takes no comparison value.`
      });
    }

    // `allowSymbolic` belongs to `partiallyCombined` only, and is pinned to the one set the grader
    // implements (√ roots + π) — see SUPPORTED_SYMBOLIC_EXCEPTIONS. A partial set (e.g. `['pi']`) would
    // imply roots are rejected, which the (currently permissive) predicate does not do.
    if (meta.needsSymbolicExceptions) {
      const allowed = new Set(literal.allowSymbolic ?? []);
      const isSupportedSet =
        allowed.size === SUPPORTED_SYMBOLIC_EXCEPTIONS.length &&
        SUPPORTED_SYMBOLIC_EXCEPTIONS.every(exception => allowed.has(exception));
      if (!isSupportedSet) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['terms', index, 'allowSymbolic'],
          message: `"${literal.policy}" requires allowSymbolic = [${SUPPORTED_SYMBOLIC_EXCEPTIONS.join(', ')}].`
        });
      }
    }
    if (!meta.needsSymbolicExceptions && literal.allowSymbolic != null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['terms', index, 'allowSymbolic'],
        message: `"${literal.policy}" takes no symbolic exceptions.`
      });
    }

    const key = `${literal.policy}:${literal.negate ? 'not' : 'is'}`;
    const opposite = `${literal.policy}:${literal.negate ? 'is' : 'not'}`;
    if (seen.has(key)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['terms', index],
        message: `Duplicate policy "${literal.policy}".`
      });
    }
    if (seen.has(opposite)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['terms', index],
        message: `"${literal.policy}" is both required and negated.`
      });
    }
    seen.add(key);

    // Group-exclusivity is an AND-only rule: two same-group policies can't BOTH hold, but either-OR is fine.
    if (connective === 'and' && !literal.negate) {
      if (usedGroups.has(meta.group)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['terms', index],
          message: `Only one ${meta.group} policy may be combined with AND.`
        });
      }
      usedGroups.add(meta.group);
    }
  });

  // A form-only policy (`combined`/`reduced`) needs a value-bearing companion (anything else — value
  // equivalence, a notation/form check, or a bound). The curated UI always emits `approx`, so this
  // only guards raw-authored expressions. (CodeAnt 140876/140877)
  const hasFormOnly = terms.some(t => !t.negate && isFormOnly(t.policy));
  const hasValueCoverage = terms.some(t => !t.negate && !isFormOnly(t.policy));
  if (hasFormOnly && !hasValueCoverage) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['terms'],
      message:
        '"combined"/"partiallyCombined"/"reduced" check form only — pair with a value policy (e.g. equivalent or approx).'
    });
  }
}

/**
 * The `mathPolicies` field shared by widget option/answer schemas: a connective + a clash-validated
 * list of literals. Absent ⇒ the grader applies the default (`equivalent`).
 */
export const mathPoliciesField = z
  .object({
    connective: z.enum(MATH_CONNECTIVES),
    terms: z.array(MathPolicyLiteralSchema).min(1)
  })
  .superRefine(validateMathPolicyExpression)
  .optional();

/** The `mathKeyboard` field shared by widget option/answer schemas. */
export const mathKeyboardField = z.enum(MATH_KEYBOARD_IDS).optional();

/** Error code for a slot expression that carries an answer (`hasOnlyEmptySlots`). */
export const MATH_SLOT_EXPRESSION_CARRIES_ANSWER_ERROR = 'mathSlotExpressionCarriesAnswer';

/**
 * The `mathSlotExpression` field of a Math blank (PBI 538086): MathLive LaTeX in which each student slot
 * is a prompt, `\placeholder[<slotId>]{}`, always empty.
 */
export const mathSlotExpressionField = z
  .string()
  .optional()
  .refine(expression => expression === undefined || hasOnlyEmptySlots(expression), {
    message: MATH_SLOT_EXPRESSION_CARRIES_ANSWER_ERROR
  });
